import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Alert from '../../../shared/components/Alert/Alert';
import Box from '../../../shared/components/Box/Box';
import Breadcrumbs from '../../../shared/components/Breadcrumbs/Breadcrumbs';
import Button from '../../../shared/components/Button/Button';
import Drawer from '../../../shared/components/Drawer/Drawer';
import EmptyState from '../../../shared/components/EmptyState/EmptyState';
import SearchInput from '../../../shared/components/SearchInput/SearchInput';
import Skeleton from '../../../shared/components/Skeleton/Skeleton';
import WikiAdminCategoriesPage from './components/WikiAdminCategoriesPage';
import WikiAdminPage from './components/WikiAdminPage';
import WikiCollectionPage from './components/WikiCollectionPage';
import WikiEditor from './components/WikiEditor';
import WikiHomePage from './components/WikiHomePage';
import WikiReaderPage from './components/WikiReaderPage';
import WikiSearchPage from './components/WikiSearchPage';
import WikiSidebar from './components/WikiSidebar';
import { MenuIcon, RefreshIcon } from './components/WikiIcons';
import {
  addFavoriteArticle,
  archiveArticle,
  autosaveWikiArticle,
  createArticle,
  createArticleComment,
  createWikiCategory,
  getArticleComments,
  getArticleRevisions,
  recordArticleView,
  removeFavoriteArticle,
  requestArticleRevision,
  restoreArticle,
  submitArticle,
  publishArticle,
  updateArticle,
  updateArticleComment,
  updateWikiCategory,
} from './wiki.api';
import {
  adminSections,
  categories,
  createEmptyCategoryDraft,
  getCategoryName,
  getRouteParts,
  isCategoryActive,
  matchArticle,
  slugify,
  statuses,
} from './wiki.constants';
import { getWikiErrorMessage } from './wiki.errors';
import { formatWikiDate } from './wiki.date';
import useWikiData from '../../../hooks/useWikiData';

const articleColumns = [
  { field: 'title', headerName: 'Article', flex: 1.5, minWidth: 260 },
  {
    field: 'category',
    headerName: 'Categorie',
    flex: 0.9,
    minWidth: 150,
    renderCell: (params) => params.row?.categoryLabel || getCategoryName(params.row?.category),
  },
  { field: 'type', headerName: 'Type', flex: 0.8, minWidth: 150 },
  { field: 'status', headerName: 'Statut', flex: 0.8, minWidth: 130 },
  { field: 'owner', headerName: 'Referent', flex: 0.8, minWidth: 140 },
  { field: 'updatedAt', headerName: 'Maj', flex: 0.7, minWidth: 120, renderCell: (params) => formatWikiDate(params.row?.updatedAt) },
];

const categoryColumns = [
  { field: 'name', headerName: 'Categorie', flex: 1, minWidth: 180 },
  { field: 'slug', headerName: 'Slug', flex: 0.9, minWidth: 160 },
  { field: 'parentLabel', headerName: 'Parent', flex: 0.8, minWidth: 150 },
  { field: 'articleCount', headerName: 'Articles', flex: 0.5, minWidth: 100 },
  { field: 'displayOrder', headerName: 'Ordre', flex: 0.5, minWidth: 100 },
  {
    field: 'isActive',
    headerName: 'Active',
    flex: 0.5,
    minWidth: 100,
    renderCell: (params) => (params.row?.isActive ? 'Oui' : 'Non'),
  },
];

function WikiPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeParts = useMemo(() => getRouteParts(location.pathname), [location.pathname]);
  const searchQueryParam = useMemo(() => (
    new URLSearchParams(location.search).get('q') || ''
  ), [location.search]);
  const statusQueryParam = useMemo(() => (
    new URLSearchParams(location.search).get('status') || ''
  ), [location.search]);
  const {
    articles, setArticles, wikiCategories, setWikiCategories, wikiArticleTypes,
    favorites, setFavorites, recentIds, setRecentIds, viewStats, wikiLoading,
    wikiError, setWikiError, wikiPermissionDenied, setWikiPermissionDenied, reload: reloadWiki,
  } = useWikiData();
  const [search, setSearch] = useState('');
  const [wikiSuccess, setWikiSuccess] = useState(null);
  const [articleRevisions, setArticleRevisions] = useState([]);
  const [articleComments, setArticleComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState(createEmptyCategoryDraft);
  const [categorySaving, setCategorySaving] = useState(false);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [adminFilters, setAdminFilters] = useState({
    category: '',
    type: '',
    owner: '',
    status: '',
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  useEffect(() => {
    setSearch(searchQueryParam);
  }, [searchQueryParam]);

  useEffect(() => {
    if (!wikiSuccess) {
      return undefined;
    }

    const timer = window.setTimeout(() => setWikiSuccess(null), 4500);
    return () => window.clearTimeout(timer);
  }, [wikiSuccess]);

  const mode = routeParts[0] || 'home';
  const routeValue = routeParts[1];
  const isEditRoute = routeParts[2] === 'edit';
  const isEditorMode = mode === 'new' || (mode === 'article' && isEditRoute);
  const showGlobalSearch = !['home', 'search'].includes(mode) && !isEditorMode;
  const showCreateAction = mode === 'home';
  const adminSection = adminSections.find((section) => section.key === (routeValue || '')) || adminSections[0];
  const selectedArticle = useMemo(() => {
    if (mode !== 'article') return null;
    return articles.find((article) => (
      String(article.id) === routeValue || article.slug === routeValue
    )) || null;
  }, [articles, mode, routeValue]);

  const getCategoryLabel = useCallback(
    (slug) => wikiCategories.find((category) => category.slug === slug)?.name || getCategoryName(slug),
    [wikiCategories],
  );

  useEffect(() => {
    let isMounted = true;

    const loadRevisions = async () => {
      if (!selectedArticle?.id || mode !== 'article' || isEditRoute) {
        setArticleRevisions([]);
        setArticleComments([]);
        return;
      }

      try {
        const [revisions, comments] = await Promise.all([
          getArticleRevisions(selectedArticle.id),
          getArticleComments(selectedArticle.id),
        ]);
        if (isMounted) {
          setArticleRevisions(Array.isArray(revisions) ? revisions : []);
          setArticleComments(Array.isArray(comments) ? comments : []);
        }
      } catch (error) {
        if (isMounted) {
          setArticleRevisions([]);
          setArticleComments([]);
        }
      }
    };

    loadRevisions();

    return () => {
      isMounted = false;
    };
  }, [isEditRoute, mode, selectedArticle?.id]);

  const categoryCounts = useMemo(() => wikiCategories.map((category) => ({
    ...category,
    count: articles.filter((article) => article.category === category.slug).length,
  })), [articles, wikiCategories]);
  const activeCategoryCounts = useMemo(() => (
    categoryCounts.filter(isCategoryActive)
  ), [categoryCounts]);

  const publishedArticles = useMemo(() => articles.filter((article) => article.status === 'Publie'), [articles]);
  const recentArticles = useMemo(() => recentIds
    .map((id) => articles.find((article) => article.id === id))
    .filter(Boolean)
    .slice(0, 4), [articles, recentIds]);
  const favoriteArticles = useMemo(() => articles.filter((article) => favorites.includes(article.id)), [articles, favorites]);

  const filteredArticles = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const baseArticles = mode === 'category'
      ? articles.filter((article) => article.category === routeValue)
      : articles;

    if (!needle) {
      return baseArticles;
    }

    return baseArticles.filter((article) => matchArticle(article, needle));
  }, [articles, mode, routeValue, search]);

  const adminRows = useMemo(() => articles.map((article) => ({
    ...article,
    categoryLabel: getCategoryLabel(article.category),
  })), [articles, getCategoryLabel]);

  const ownerOptions = useMemo(() => (
    [...new Set(articles.map((article) => article.owner).filter(Boolean))].sort()
  ), [articles]);

  const adminFilteredRows = useMemo(() => adminRows.filter((article) => {
    if (adminSection.status && article.status !== adminSection.status) {
      return false;
    }

    if (adminFilters.category && article.category !== adminFilters.category) {
      return false;
    }

    if (adminFilters.type && article.type !== adminFilters.type) {
      return false;
    }

    if (adminFilters.owner && article.owner !== adminFilters.owner) {
      return false;
    }

    if (!adminSection.status && adminFilters.status && article.status !== adminFilters.status) {
      return false;
    }

    return true;
  }), [adminFilters, adminRows, adminSection.status]);

  const categoryRows = useMemo(() => wikiCategories.map((category) => ({
    ...category,
    articleCount: articles.filter((article) => article.category === category.slug).length,
    parentLabel: category.parent ? getCategoryLabel(category.parent) : '-',
  })), [articles, wikiCategories, getCategoryLabel]);

  const mostViewedArticle = useMemo(() => {
    const topStat = viewStats[0];
    if (!topStat) {
      return null;
    }

    const article = articles.find((currentArticle) => String(currentArticle.id) === String(topStat.articleId));
    return article ? { ...article, views: Number(topStat.views || 0) } : null;
  }, [articles, viewStats]);
  const topbarContext = useMemo(() => {
    const base = {
      breadcrumbs: [{ label: 'Wiki', onClick: () => navigate('/Wiki') }],
      subtitle: 'Retrouvez les procédures, méthodes, référentiels et retours d\'experience de l\'entreprise.',
      title: 'Base de connaissances',
    };

    if (mode === 'home') {
      return base;
    }

    if (mode === 'article') {
      const articleTitle = selectedArticle?.title || (isEditRoute ? 'Edition' : 'Article');
      const categoryLabel = selectedArticle?.category ? getCategoryLabel(selectedArticle.category) : null;
      return {
        breadcrumbs: [
          ...base.breadcrumbs,
          categoryLabel && { label: categoryLabel, onClick: () => navigate(`/Wiki/category/${selectedArticle.category}`) },
          { label: isEditRoute ? `${articleTitle} - edition` : articleTitle },
        ],
        subtitle: isEditRoute ? 'Rédaction et publication documentaire' : 'Consultation documentaire',
        title: articleTitle,
      };
    }

    if (mode === 'new') {
      return {
        breadcrumbs: [...base.breadcrumbs, { label: 'Nouvel article' }],
        subtitle: 'Rédaction et publication documentaire',
        title: 'Nouvel article',
      };
    }

    if (mode === 'category') {
      const categoryLabel = getCategoryLabel(routeValue);
      return {
        breadcrumbs: [...base.breadcrumbs, { label: categoryLabel }],
        subtitle: 'Articles classes dans cette catégorie',
        title: categoryLabel,
      };
    }

    if (mode === 'search') {
      return {
        breadcrumbs: [...base.breadcrumbs, { label: 'Recherche' }],
        subtitle: 'Résultats documentaires filtres par pertinence ou date',
        title: 'Recherche documentaire',
      };
    }

    if (mode === 'favorites') {
      return {
        breadcrumbs: [...base.breadcrumbs, { label: 'Favoris' }],
        subtitle: 'Articles marqués pour une consultation rapide',
        title: 'Favoris',
      };
    }

    if (mode === 'recent') {
      return {
        breadcrumbs: [...base.breadcrumbs, { label: 'Consultés récemment' }],
        subtitle: 'Reprendre les lectures et travaux récents',
        title: 'Consultés récemment',
      };
    }

    if (mode === 'admin' && routeValue === 'categories') {
      return {
        breadcrumbs: [...base.breadcrumbs, { label: 'Gestion', onClick: () => navigate('/Wiki/admin') }, { label: 'Categories' }],
        subtitle: 'Arborescence, ordre et statut des catégories',
        title: 'Gestion des catégories',
      };
    }

    if (mode === 'admin') {
      return {
        breadcrumbs: [...base.breadcrumbs, { label: 'Gestion' }, { label: adminSection.label }],
        subtitle: 'Pilotage des articles, workflows et validations',
        title: `Administration - ${adminSection.label}`,
      };
    }

    return base;
  }, [adminSection.label, getCategoryLabel, isEditRoute, mode, navigate, routeValue, selectedArticle]);

  const setArticleAndOpen = (article) => {
    setRecentIds((current) => [article.id, ...current.filter((id) => id !== article.id)].slice(0, 8));
    recordArticleView(article.id).catch(() => {});
    navigate(`/Wiki/article/${article.slug || article.id}`);
  };

  const setWikiActionError = (error, fallbackMessage) => {
    setWikiSuccess(null);
    setWikiPermissionDenied(error.status === 403);
    setWikiError(getWikiErrorMessage(error, fallbackMessage));
  };

  const toggleFavorite = (articleId) => {
    const isFavorite = favorites.includes(articleId);
    setFavorites((current) => (
      isFavorite
        ? current.filter((id) => id !== articleId)
        : [...current, articleId]
    ));

    const request = isFavorite
      ? removeFavoriteArticle(articleId)
      : addFavoriteArticle(articleId);

    request
      .then((nextFavorites) => {
        if (Array.isArray(nextFavorites)) {
          setFavorites(nextFavorites);
        }
      })
      .catch(() => {
        setFavorites((current) => (
          isFavorite
            ? [...current, articleId]
            : current.filter((id) => id !== articleId)
        ));
      });
  };

  const submitComment = async (articleId) => {
    if (!commentDraft.trim()) {
      return;
    }

    setCommentSaving(true);
    try {
      const comment = await createArticleComment(articleId, { content: commentDraft });
      if (comment) {
        setArticleComments((current) => [comment, ...current]);
      }
      setCommentDraft('');
      setWikiError(null);
      setWikiPermissionDenied(false);
      setWikiSuccess('Proposition enregistree.');
    } catch (error) {
      setWikiActionError(error, 'Impossible d enregistrer la proposition.');
    } finally {
      setCommentSaving(false);
    }
  };

  const updateComment = async (articleId, commentId, payload) => {
    try {
      const updatedComment = await updateArticleComment(articleId, commentId, payload);
      setArticleComments((current) => current.map((comment) => (
        comment.id === updatedComment.id ? updatedComment : comment
      )));
      setWikiError(null);
      setWikiPermissionDenied(false);
      setWikiSuccess('Proposition mise a jour.');
    } catch (error) {
      setWikiActionError(error, 'Impossible de mettre a jour la proposition.');
    }
  };

  const saveArticle = async (draft) => {
    try {
      if (draft.id) {
        const updated = await updateArticle(draft.id, draft);
        setArticles((current) => current.map((article) => (
          article.id === updated.id ? updated : article
        )));
        setWikiError(null);
        setWikiPermissionDenied(false);
        setWikiSuccess('Article enregistre.');
        navigate(`/Wiki/article/${updated.slug || updated.id}`);
        return;
      }

      const article = await createArticle(draft);
      setArticles((current) => [article, ...current]);
      setWikiError(null);
      setWikiPermissionDenied(false);
      setWikiSuccess('Article cree.');
      navigate(`/Wiki/article/${article.slug || article.id}`);
    } catch (error) {
      setWikiActionError(error, 'Impossible d enregistrer l article.');
      return;
    }
  };

  const autosaveArticle = async (draft) => {
    if (!draft.id) {
      return null;
    }

    const updated = await autosaveWikiArticle(draft.id, draft);
    setArticles((current) => current.map((article) => (
      article.id === updated.id ? updated : article
    )));
    return updated;
  };

  const updateCategoryDraft = (field, value) => {
    setCategoryDraft((current) => ({ ...current, [field]: value }));
  };

  const resetCategoryDraft = () => {
    setCategoryDraft(createEmptyCategoryDraft());
  };

  const editCategory = (category) => {
    setCategoryDraft({
      id: category.id,
      name: category.name || '',
      slug: category.slug || '',
      parent: category.parent || '',
      description: category.description || '',
      icon: category.icon || 'folder',
      displayOrder: category.displayOrder || 0,
      isActive: isCategoryActive(category),
    });
  };

  const saveCategory = async () => {
    setCategorySaving(true);
    try {
      const payload = {
        ...categoryDraft,
        displayOrder: Number(categoryDraft.displayOrder || 0),
        slug: categoryDraft.slug || slugify(categoryDraft.name),
      };
      const nextCategories = categoryDraft.id
        ? await updateWikiCategory(categoryDraft.id, payload)
        : await createWikiCategory(payload);

      setWikiCategories(Array.isArray(nextCategories) && nextCategories.length ? nextCategories : categories);
      resetCategoryDraft();
      setWikiError(null);
      setWikiPermissionDenied(false);
      setWikiSuccess('Categorie enregistree.');
    } catch (error) {
      setWikiActionError(error, 'Impossible d enregistrer la categorie.');
    } finally {
      setCategorySaving(false);
    }
  };

  const updateAdminFilter = (field, value) => {
    setAdminFilters((current) => ({ ...current, [field]: value }));
    setPaginationModel((current) => ({ ...current, page: 0 }));
  };

  const applyArticleStatus = async (articleId, action) => {
    const actionMap = {
      submit: submitArticle,
      publish: publishArticle,
      revise: requestArticleRevision,
      archive: archiveArticle,
      restore: restoreArticle,
    };
    const request = actionMap[action];

    if (!request) {
      return;
    }

    try {
      const updated = await request(articleId);
      setArticles((current) => current.map((article) => (
        article.id === updated.id ? updated : article
      )));
      setWikiError(null);
      setWikiPermissionDenied(false);
      setWikiSuccess('Statut de l article mis a jour.');
    } catch (error) {
      setWikiActionError(error, 'Impossible de changer le statut de l article.');
    }
  };

  const renderWorkflowActions = (article) => {
    const actionsByStatus = {
      Brouillon: [{ action: 'submit', label: 'Soumettre' }],
      'A valider': [
        { action: 'publish', label: 'Publier' },
        { action: 'revise', label: 'A réviser' },
      ],
      Publie: [
        { action: 'revise', label: 'A réviser' },
        { action: 'archive', label: 'Archiver' },
      ],
      'A réviser': [{ action: 'publish', label: 'Republier' }],
      Archive: [{ action: 'restore', label: 'Restaurer' }],
    };

    return (
      <div className="wiki-row-actions">
        <Button size="small" variant="secondary" onClick={(event) => {
          event.stopPropagation();
          navigate(`/Wiki/article/${article.id}/edit`);
        }}>
          Modifier
        </Button>
        {(actionsByStatus[article.status] || []).map((item) => (
          <Button key={item.action} size="small" variant="tertiary" onClick={(event) => {
            event.stopPropagation();
            applyArticleStatus(article.id, item.action);
          }}>
            {item.label}
          </Button>
        ))}
      </div>
    );
  };

  const renderSidebar = () => (
    <WikiSidebar
      mode={mode}
      routeValue={routeValue}
      categories={activeCategoryCounts}
      articles={articles}
      navigate={navigate}
    />
  );

  const renderMobileSidebar = () => (
    <WikiSidebar
      mode={mode}
      routeValue={routeValue}
      categories={activeCategoryCounts}
      articles={articles}
      navigate={(nextPath) => {
        setMobileNavigationOpen(false);
        navigate(nextPath);
      }}
    />
  );

  const renderHome = () => (
    <WikiHomePage
      articles={articles}
      categories={activeCategoryCounts}
      favorites={favorites}
      getCategoryLabel={getCategoryLabel}
      navigate={navigate}
      onOpenArticle={setArticleAndOpen}
      onSearchChange={setSearch}
      search={search}
      onToggleFavorite={toggleFavorite}
      publishedArticles={publishedArticles}
      recentArticles={recentArticles}
    />
  );

  const renderSearchOrCategory = () => (
    <WikiSearchPage
      getCategoryLabel={getCategoryLabel}
      mode={mode}
      onOpenArticle={setArticleAndOpen}
      onSearchChange={setSearch}
      routeValue={routeValue}
      rows={filteredArticles}
      search={search}
      initialStatusFilter={statusQueryParam}
    />
  );

  const renderArticle = () => {
    const relatedArticles = selectedArticle?.relatedArticles
      ? selectedArticle.relatedArticles
        .map((id) => articles.find((article) => article.id === id))
        .filter(Boolean)
      : [];

    return (
      <WikiReaderPage
        article={selectedArticle}
        articleComments={articleComments}
        articleRevisions={articleRevisions}
        commentDraft={commentDraft}
        commentSaving={commentSaving}
        favorites={favorites}
        getCategoryLabel={getCategoryLabel}
        onCommentDraftChange={setCommentDraft}
        onEdit={(article) => navigate(`/Wiki/article/${article.id}/edit`)}
        onOpenArticle={setArticleAndOpen}
        onSubmitComment={submitComment}
        onUpdateComment={updateComment}
        onToggleFavorite={toggleFavorite}
        relatedArticles={relatedArticles}
      />
    );
  };

  const renderEditor = () => {
    const sourceArticle = isEditRoute ? selectedArticle : null;
    return (
      <WikiEditor
        article={sourceArticle}
        articles={articles}
        categories={wikiCategories}
        articleTypes={wikiArticleTypes}
        getCategoryLabel={getCategoryLabel}
        onAutosave={autosaveArticle}
        onCancel={() => navigate(sourceArticle ? `/Wiki/article/${sourceArticle.slug}` : '/Wiki')}
        onSave={saveArticle}
      />
    );
  };

  const renderCollection = (title, rows) => (
    <WikiCollectionPage
      favorites={favorites}
      getCategoryLabel={getCategoryLabel}
      onOpenArticle={setArticleAndOpen}
      onToggleFavorite={toggleFavorite}
      rows={rows}
      title={title}
    />
  );

  const renderAdminCategories = () => (
    <WikiAdminCategoriesPage
      categoryColumns={categoryColumns}
      categoryDraft={categoryDraft}
      categoryRows={categoryRows}
      categorySaving={categorySaving}
      navigate={navigate}
      onDraftChange={updateCategoryDraft}
      onEditCategory={editCategory}
      onPaginationModelChange={setPaginationModel}
      onResetDraft={resetCategoryDraft}
      onSaveCategory={saveCategory}
      paginationModel={paginationModel}
      wikiCategories={wikiCategories}
    />
  );

  const renderAdmin = () => (
    <WikiAdminPage
      adminFilters={adminFilters}
      adminSection={adminSection}
      articleColumns={articleColumns}
      articles={articles}
      filteredRows={adminFilteredRows}
      mostViewedArticle={mostViewedArticle}
      navigate={navigate}
      onFilterChange={updateAdminFilter}
      onPaginationModelChange={setPaginationModel}
      onResetFilters={() => setAdminFilters({ category: '', type: '', owner: '', status: '' })}
      ownerOptions={ownerOptions}
      paginationModel={paginationModel}
      renderWorkflowActions={renderWorkflowActions}
      statuses={statuses}
      wikiArticleTypes={wikiArticleTypes}
      wikiCategories={wikiCategories}
    />
  );

  const renderContent = () => {
    if (mode === 'article' && isEditRoute) return renderEditor();
    if (mode === 'new') return renderEditor();
    if (mode === 'article') return renderArticle();
    if (mode === 'search' || mode === 'category') return renderSearchOrCategory();
    if (mode === 'favorites') return renderCollection('Favoris', favoriteArticles);
    if (mode === 'recent') return renderCollection('Articles recents', recentArticles);
    if (mode === 'admin' && routeValue === 'categories') return renderAdminCategories();
    if (mode === 'admin') return renderAdmin();
    return renderHome();
  };
  const renderWikiLoadingState = () => (
    <div className="wiki-loading-state" role="status" aria-live="polite">
      <span className="sr-only">Chargement du wiki...</span>
      <div className="wiki-loading-state__header">
        <Skeleton size="large" />
        <Skeleton size="medium" />
      </div>
      <div className="wiki-loading-state__grid">
        <Skeleton className="wiki-loading-state__card" size="large" count={3} />
      </div>
      <div className="wiki-loading-state__list">
        <Skeleton size="medium" count={5} />
      </div>
    </div>
  );
  const renderPermissionDeniedState = () => (
    <EmptyState title="Accès refusé">
      Vous n'avez pas les droits nécessaires pour consulter ou modifier cet espace documentaire.
    </EmptyState>
  );

  return (
    <Box className="page-container module-page wiki-page">
      <div className="wiki-shell">
        <header className="wiki-topbar">
          <div className="wiki-topbar-context">
            <Breadcrumbs items={topbarContext.breadcrumbs} />
            <span className="custom-breadcrumbs">{topbarContext.subtitle}</span>
          </div>
          <div className="wiki-topbar-actions">
            <Button
              className="wiki-mobile-nav-button"
              variant="secondary"
              iconStart={<MenuIcon fontSize="small" />}
              onClick={() => setMobileNavigationOpen(true)}
            >
              Navigation
            </Button>
            {showGlobalSearch && (
              <SearchInput
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  if (mode !== 'search') navigate('/Wiki/search');
                }}
                placeholder="Rechercher une procédure, un projet ou une référence..."
              />
            )}
            {showCreateAction && (
              <Button variant="primary" onClick={() => navigate('/Wiki/new')}>
                Nouvel article
              </Button>
            )}
          </div>
        </header>

        <Drawer
          labelledBy="wiki-mobile-navigation-title"
          open={mobileNavigationOpen}
          title="Navigation Wiki"
          onClose={() => setMobileNavigationOpen(false)}
        >
          {renderMobileSidebar()}
        </Drawer>

        <div className="wiki-layout">
          <aside className="module-panel wiki-sidebar-panel">{renderSidebar()}</aside>
          <main className="module-panel wiki-content-panel" aria-busy={wikiLoading}>
              {wikiError && (
                <Alert
                  severity="warning"
                  action={(
                    <Button size="small" variant="secondary" iconStart={<RefreshIcon fontSize="small" />} onClick={reloadWiki}>
                      Réessayer
                    </Button>
                  )}
                >
                  {wikiError}
                </Alert>
              )}
              {wikiSuccess && <Alert severity="success">{wikiSuccess}</Alert>}
              {wikiLoading ? renderWikiLoadingState() : (wikiPermissionDenied ? renderPermissionDeniedState() : renderContent())}
          </main>
        </div>
      </div>
    </Box>
  );
}

export default WikiPage;
